import { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAuthentication from "../../hooks/useAuthentication";
import { updateUserProfile } from "../../utility/httpActions/userActions";
import {
  shopCategories,
  COUNTRY_LABELS_BY_CODE,
} from "../../utility/constants";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";
import {
  NotificationPreferencesSection,
  SWITCH_SX,
} from "./NotificationPreferencesSection";
import WhatsAppIcon from "../../common-components/Icons/WhatsAppIcon";
import LogoutConfirmDialog from "../../common-components/LogoutConfirmDialog";
import { getCurrencyLabelForCountry } from "../../utility/utilityFunctions";

const countryCodeToFlag = (code) => {
  if (!code || code.length !== 2) return "";
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
};

const formatAddress = (addr) => {
  if (!addr) return "";
  const line1 = [addr.addressLine1, addr.addressLine2]
    .filter(Boolean)
    .join(", ");
  const locality = [addr.city, addr.lga].filter(Boolean).join(", ");
  const line2 = [locality, addr.state, addr.postalCode, addr.country]
    .filter(Boolean)
    .join(", ");
  return [line1, line2, addr.addressDescription].filter(Boolean).join("\n");
};

const VISIBILITY_OPTIONS = [
  { value: "PUBLIC", label: "Everyone" },
  { value: "FOLLOWERS_ONLY", label: "Followers only" },
  { value: "PRIVATE", label: "Only me" },
];

const VISIBILITY_LABELS = {
  PUBLIC: "Everyone",
  FOLLOWERS_ONLY: "Followers only",
  PRIVATE: "Only me",
};

// Mirrors ngwaahia-mobile's GetVerifiedScreen.js status copy
const VERIFICATION_STATUS_LABELS = {
  NOT_SUBMITTED: "Not verified",
  PENDING: "Verification pending",
  APPROVED: "Verified",
  REJECTED: "Verification rejected",
};

const VERIFICATION_STATUS_COLORS = {
  NOT_SUBMITTED: "#9ca3af",
  PENDING: "#d97706",
  APPROVED: "#16a34a",
  REJECTED: "#dc2626",
};

const SectionHeader = ({ children, expanded, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      bgcolor: "#f9fafb",
      px: 2,
      py: 0.75,
      mt: 1,
      cursor: "pointer",
      userSelect: "none",
      "&:hover": { bgcolor: "#f3f4f6" },
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 1,
        color: "text.secondary",
      }}
    >
      {children}
    </Typography>
    <ExpandMoreIcon
      sx={{
        fontSize: 18,
        color: "#9ca3af",
        transition: "transform 250ms",
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      }}
    />
  </Box>
);

const SettingsRow = ({
  icon: Icon,
  iconColor = "#046287",
  primary,
  secondary,
  right,
  onClick,
  disabled,
}) => (
  <ListItem
    onClick={disabled ? undefined : onClick}
    sx={{
      cursor: onClick && !disabled ? "pointer" : "default",
      "&:hover": onClick && !disabled ? { bgcolor: "#f9fafb" } : {},
      opacity: disabled ? 0.5 : 1,
      py: 1.25,
    }}
  >
    <ListItemIcon sx={{ minWidth: 40 }}>
      <Icon sx={{ fontSize: 22, color: iconColor }} />
    </ListItemIcon>
    <ListItemText
      primary={primary}
      secondary={secondary}
      primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
      secondaryTypographyProps={{ fontSize: 12 }}
    />
    {right}
  </ListItem>
);

const VisibilityPicker = ({ value, onChange, disabled }) => (
  <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
    {VISIBILITY_OPTIONS.map((opt) => (
      <FormControlLabel
        key={opt.value}
        value={opt.value}
        control={
          <Radio size="small" sx={{ "&.Mui-checked": { color: "#046287" } }} />
        }
        label={
          <Typography variant="body2" fontSize={12}>
            {opt.label}
          </Typography>
        }
        disabled={disabled}
        sx={{ mr: 2 }}
      />
    ))}
  </RadioGroup>
);

const AppOnlyChip = () => (
  <Chip
    icon={<SmartphoneOutlinedIcon sx={{ fontSize: "14px !important" }} />}
    label="App only"
    size="small"
    variant="outlined"
    sx={{
      fontSize: 11,
      height: 24,
      color: "text.secondary",
      borderColor: "#e5e7eb",
    }}
  />
);

const AccountSettings = () => {
  const queryClient = useQueryClient();
  const { currentUser, setCurrentUser, handleLogout, isLoggingOut } =
    useAuthentication();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const whatsappEnabled = useFeatureFlag("whatsapp");
  const emailNotificationsEnabled = useFeatureFlag("email-notifications");
  const [snackbar, setSnackbar] = useState(null);
  const [expandedPrivacy, setExpandedPrivacy] = useState(null);

  const [collapsed, setCollapsed] = useState({});
  const toggle = (key) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  const isOpen = (key) => !collapsed[key];

  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (data) => {
      if (data?.user) setCurrentUser(data.user);
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
    onError: () =>
      setSnackbar({
        severity: "error",
        message: "Failed to update settings. Please try again.",
      }),
  });

  if (!currentUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const privacy = currentUser.privacySettings || {};
  const notifPrefs = currentUser.notificationPreferences || {};
  const interests = currentUser.interests || [];
  const addresses = currentUser.addresses || [];

  const updatePrivacy = (field, value) => {
    updateProfile({ privacySettings: { [field]: value } });
  };

  const updateNotifEnabled = (value) => {
    updateProfile({ notificationPreferences: { enabled: value } });
  };

  const updateNotifEvent = (event, value) => {
    updateProfile({ notificationPreferences: { events: { [event]: value } } });
  };

  const updateNotifChannel = (channel, value) => {
    updateProfile({
      notificationPreferences: { channels: { [channel]: value } },
    });
  };

  const toggleInterest = (categoryValue) => {
    const current = [...interests];
    const idx = current.indexOf(categoryValue);
    if (idx > -1) {
      current.splice(idx, 1);
    } else if (current.length < 10) {
      current.push(categoryValue);
    }
    updateProfile({ interests: current });
  };

  const togglePrivacy = (field) => {
    setExpandedPrivacy(expandedPrivacy === field ? null : field);
  };

  const privacyFields = [
    {
      field: "emailVisibility",
      label: "Email visibility",
      icon: EmailOutlinedIcon,
      subtitle: "Who can see your email address",
      defaultValue: "PRIVATE",
    },
    {
      field: "phoneVisibility",
      label: "Phone visibility",
      icon: PhoneOutlinedIcon,
      subtitle: "Who can see your phone number",
      defaultValue: "PRIVATE",
    },
    {
      field: "followListVisibility",
      label: "Follow list visibility",
      icon: PeopleOutlinedIcon,
      subtitle: "Who can see who you follow",
      defaultValue: "FOLLOWERS_ONLY",
    },
    {
      field: "experienceVisibility",
      label: "Experience visibility",
      icon: WorkOutlineIcon,
      subtitle: "Who can see your experience",
      defaultValue: "FOLLOWERS_ONLY",
    },
    {
      field: "educationVisibility",
      label: "Education visibility",
      icon: SchoolOutlinedIcon,
      subtitle: "Who can see your education",
      defaultValue: "FOLLOWERS_ONLY",
    },
    {
      field: "locationVisibility",
      label: "Location visibility",
      icon: LocationOnOutlinedIcon,
      subtitle: "Who can see your location",
      defaultValue: "PRIVATE",
    },
  ];

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", pb: 8 }}>
      {/* Header with sticky loading bar */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 10, bgcolor: "white" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: { xs: 2, md: 0 },
            pt: 4,
            pb: 1,
          }}
        >
          <SettingsOutlinedIcon sx={{ color: "#046287" }} />
          <Typography variant="h5" fontWeight={700}>
            Account Settings
          </Typography>
        </Box>
        <Fade in={isPending}>
          <LinearProgress
            sx={{
              height: 2,
              "& .MuiLinearProgress-bar": { bgcolor: "#046287" },
            }}
          />
        </Fade>
      </Box>

      <List disablePadding>
        {/* Account */}
        <SectionHeader
          expanded={isOpen("account")}
          onToggle={() => toggle("account")}
        >
          Account
        </SectionHeader>
        <Collapse in={isOpen("account")} timeout={250}>
          <Box sx={{ bgcolor: "white" }}>
            <SettingsRow
              icon={PersonOutlinedIcon}
              primary={currentUser.name || "—"}
              secondary="Name"
              right={<AppOnlyChip />}
            />
            <Divider variant="inset" component="li" />
            <SettingsRow
              icon={PhoneOutlinedIcon}
              primary={currentUser.phoneNumber || "—"}
              secondary="Phone"
              right={<AppOnlyChip />}
            />
            {currentUser.phoneNumber && (
              <>
                <Divider variant="inset" component="li" />
                <SettingsRow
                  icon={WhatsAppIcon}
                  primary="This number is on WhatsApp"
                  secondary="We may use WhatsApp to send you updates"
                  right={
                    <Switch
                      checked={!!currentUser.isWhatsApp}
                      onChange={(e) =>
                        updateProfile({ isWhatsApp: e.target.checked })
                      }
                      disabled={isPending}
                      size="small"
                      sx={SWITCH_SX}
                    />
                  }
                />
              </>
            )}
            <Divider variant="inset" component="li" />
            <SettingsRow
              icon={EmailOutlinedIcon}
              primary={currentUser.email || "—"}
              secondary="Email"
              right={<AppOnlyChip />}
            />
            <Divider variant="inset" component="li" />
            <SettingsRow
              icon={PublicOutlinedIcon}
              primary={
                currentUser.country
                  ? `${countryCodeToFlag(currentUser.country)}  ${COUNTRY_LABELS_BY_CODE[currentUser.country] || currentUser.country}`
                  : "—"
              }
              secondary="Country · set by your phone number"
            />
            <Divider variant="inset" component="li" />
            <SettingsRow
              icon={PaymentsOutlinedIcon}
              primary={getCurrencyLabelForCountry(currentUser.country) ?? "–"}
              secondary="Currency · set by your phone number"
            />
            <Divider variant="inset" component="li" />
            <SettingsRow
              icon={VerifiedUserOutlinedIcon}
              iconColor={
                VERIFICATION_STATUS_COLORS[
                  currentUser.verificationStatus || "NOT_SUBMITTED"
                ]
              }
              primary={
                VERIFICATION_STATUS_LABELS[
                  currentUser.verificationStatus || "NOT_SUBMITTED"
                ]
              }
              secondary={
                currentUser.identityVerificationRequired
                  ? "Re-verification required — use the Ngwaahia app to resubmit"
                  : "Identity verification"
              }
              right={<AppOnlyChip />}
            />
          </Box>
        </Collapse>

        {/* Addresses */}
        <SectionHeader
          expanded={isOpen("addresses")}
          onToggle={() => toggle("addresses")}
        >
          Addresses
        </SectionHeader>
        <Collapse in={isOpen("addresses")} timeout={250}>
          <Box sx={{ bgcolor: "white" }}>
            {addresses.length === 0 ? (
              <SettingsRow
                icon={MapOutlinedIcon}
                primary="No addresses saved"
                secondary="Add addresses in the Ngwaahia app"
                right={<AppOnlyChip />}
              />
            ) : (
              <Box sx={{ px: 2, py: 1.5 }}>
                {addresses.map((addr, i) => (
                  <Box
                    key={addr._id || addr.id || i}
                    sx={{
                      p: 1.5,
                      mb: i < addresses.length - 1 ? 1 : 0,
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                      bgcolor: "#f9fafb",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.25 }}
                    >
                      {addr.addressDescription ||
                        addr.addressee ||
                        addr.addressLine1 ||
                        "Address"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.6,
                      }}
                    >
                      {formatAddress(addr)}
                    </Typography>
                  </Box>
                ))}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  To add or edit addresses, use the Ngwaahia app.
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>

        {/* Sign-in Methods */}
        <SectionHeader
          expanded={isOpen("signin")}
          onToggle={() => toggle("signin")}
        >
          Sign-in Methods
        </SectionHeader>
        <Collapse in={isOpen("signin")} timeout={250}>
          <Box sx={{ bgcolor: "white" }}>
            <SettingsRow
              icon={KeyOutlinedIcon}
              primary="Password & Google"
              secondary="Manage sign-in methods"
              right={<AppOnlyChip />}
            />
          </Box>
        </Collapse>

        {/* Privacy */}
        <SectionHeader
          expanded={isOpen("privacy")}
          onToggle={() => toggle("privacy")}
        >
          Privacy
        </SectionHeader>
        <Collapse in={isOpen("privacy")} timeout={250}>
          <Box sx={{ bgcolor: "white" }}>
            {privacyFields.map((pf, i) => (
              <Box key={pf.field}>
                {i > 0 && <Divider variant="inset" component="li" />}
                <SettingsRow
                  icon={pf.icon}
                  primary={pf.label}
                  secondary={
                    VISIBILITY_LABELS[privacy[pf.field] ?? pf.defaultValue]
                  }
                  onClick={() => togglePrivacy(pf.field)}
                />
                <Collapse in={expandedPrivacy === pf.field} timeout={200}>
                  <Box sx={{ pl: 7, pr: 2, pb: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: "block" }}
                    >
                      {pf.subtitle}
                    </Typography>
                    <VisibilityPicker
                      value={privacy[pf.field] ?? pf.defaultValue}
                      onChange={(val) => updatePrivacy(pf.field, val)}
                      disabled={isPending}
                    />
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        </Collapse>

        {/* Notifications */}
        <SectionHeader
          expanded={isOpen("notifications")}
          onToggle={() => toggle("notifications")}
        >
          Notifications
        </SectionHeader>
        <Collapse in={isOpen("notifications")} timeout={250}>
          <Box sx={{ bgcolor: "white" }}>
            <SettingsRow
              icon={NotificationsOutlinedIcon}
              primary="All notifications"
              secondary="Master on/off for all alerts"
              right={
                <Switch
                  checked={notifPrefs.enabled !== false}
                  onChange={(e) => updateNotifEnabled(e.target.checked)}
                  disabled={isPending}
                  sx={SWITCH_SX}
                />
              }
            />

            <Collapse in={notifPrefs.enabled !== false} timeout={250}>
              <NotificationPreferencesSection
                notifPrefs={notifPrefs}
                onUpdateEvent={updateNotifEvent}
                onUpdateChannel={updateNotifChannel}
                isPending={isPending}
                whatsappEnabled={whatsappEnabled}
                emailNotificationsEnabled={emailNotificationsEnabled}
                userEmail={currentUser.email}
                isEmailVerified={currentUser.isEmailVerified}
              />
            </Collapse>
          </Box>
        </Collapse>

        {/* Preferences — interests + feed */}
        <SectionHeader
          expanded={isOpen("preferences")}
          onToggle={() => toggle("preferences")}
        >
          Preferences
        </SectionHeader>
        <Collapse in={isOpen("preferences")} timeout={250}>
          <Box sx={{ bgcolor: "white" }}>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                Product interests
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                {interests.length
                  ? `${interests.length} ${interests.length === 1 ? "category" : "categories"} selected — select up to 10`
                  : "Select up to 10 categories to personalise your feed"}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                {shopCategories.map((cat) => {
                  const selected = interests.includes(cat.value);
                  return (
                    <Chip
                      key={cat.value}
                      label={`${cat.icon} ${cat.label}`}
                      size="small"
                      onClick={() => toggleInterest(cat.value)}
                      disabled={
                        isPending || (!selected && interests.length >= 10)
                      }
                      sx={{
                        cursor: "pointer",
                        bgcolor: selected ? "#046287" : undefined,
                        color: selected ? "white" : undefined,
                        "&:hover": {
                          bgcolor: selected ? "#035070" : undefined,
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            <Divider sx={{ mx: 2 }} />
            <SettingsRow
              icon={StarOutlineIcon}
              primary="Show reviews in feed"
              secondary="Display review cards between products on your home feed"
              right={
                <Switch
                  checked={currentUser.feedPreferences?.showReviews !== false}
                  onChange={(e) =>
                    updateProfile({
                      feedPreferences: { showReviews: e.target.checked },
                    })
                  }
                  disabled={isPending}
                  sx={SWITCH_SX}
                />
              }
            />
          </Box>
        </Collapse>

        {/* Logout */}
        <SectionHeader
          expanded={isOpen("logout")}
          onToggle={() => toggle("logout")}
        >
          {" "}
        </SectionHeader>
        <Collapse in={isOpen("logout")} timeout={250}>
          <Box sx={{ bgcolor: "white" }}>
            <SettingsRow
              icon={LogoutIcon}
              iconColor="#dc2626"
              primary="Log out"
              onClick={() => setLogoutDialogOpen(true)}
            />
          </Box>
        </Collapse>

        {/* Danger Zone */}
        <SectionHeader
          expanded={isOpen("danger")}
          onToggle={() => toggle("danger")}
        >
          Danger Zone
        </SectionHeader>
        <Collapse in={isOpen("danger")} timeout={250}>
          <Box sx={{ bgcolor: "white" }}>
            <SettingsRow
              icon={WarningAmberIcon}
              iconColor="#dc2626"
              primary="Delete account / Close shop"
              secondary="Manage in the Ngwaahia app"
              right={<AppOnlyChip />}
            />
          </Box>
        </Collapse>
      </List>

      <LogoutConfirmDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={() => handleLogout()}
        isPending={isLoggingOut}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AccountSettings;
